import { AxiosInstance, AxiosStatic } from "axios";
import { useApiBlocksStore } from "@/stores/apiBlocks";
import { useNotificationsStore } from "@/stores/notifications";
import { IData } from "types/AutoComplete/IData";
import { getCurrentInstance, ref, Ref } from "vue";

export default (name: string) => {
  const app = getCurrentInstance();

  if (app === null) {
    throw new Error("App is not defined");
  }

  const $http: AxiosStatic | AxiosInstance =
    app.appContext.config.globalProperties.$http;

  const apiBlocksStore = useApiBlocksStore();
  const notificationsStore = useNotificationsStore();

  interface Items {
    current_page: number;
    total_records: number;
  }

  interface ItemData extends IData {
    id: string;
    name: string;
  }

  type NextKey = {
    post_id: string;
    published_at: string;
  };

  type IParams = {
    params: {
      per_page: number;
      feed_id?: string | null;
      feed?: string | null;
      id?: string;
      next?: string;
      published_at?: string;
      search?: string;
      current_page?: number;
      category_id?: string | null;
    };
  };

  const itemQuery: Ref<string | undefined> = ref("");
  const selectedItem: Ref<any | undefined> = ref(undefined);
  const feedQuery: Ref<string | undefined> = ref("");
  const activeFeed: Ref<string | null> = ref(null);
  const selectedFeed: Ref<ItemData | undefined> = ref(undefined);
  const feedsData: Ref<ItemData[]> = ref([]);
  const feeds: Ref<Items | null> = ref(null); //feeds with pagination inside
  const itemsData: Ref<ItemData[]> = ref([]);
  const items: Ref<Items | null> = ref(null); //products with pagination inside,
  const feedLoading = ref(false);
  const itemLoading = ref(false);
  const isInitItem = ref(false);
  const isInitFeed = ref(false);
  const nextArticleKey: Ref<NextKey | undefined> = ref(undefined);
  const nextFeedKey: Ref<NextKey | undefined> = ref(undefined);
  const categoriesData: Ref<ItemData[]> = ref([]);
  const categories: Ref<Items | null> = ref(null);
  const categoryLoading = ref(false);
  const activeCategory: Ref<string | null> = ref(null);
  const productPerPage = 5;

  const getItems = async (
    productId = undefined,
    queryString?: string | undefined,
    currentPage?: number
  ) => {
    const blockProperties = apiBlocksStore.blocks.get(name);
    if (blockProperties === undefined) {
      return;
    }

    if (blockProperties.itemsURL === undefined) {
      console.error("Items URL is not defined in Topol Options");
      return;
    }

    itemLoading.value = true;

    try {
      const params: IParams = {
        params: {
          per_page: productPerPage,
          feed: activeFeed.value,
          feed_id: activeFeed.value,
          id: productId,
          category_id: activeCategory.value,
        },
      };

      if (!blockProperties.nextKey) {
        params.params.current_page = currentPage;
        params.params.search = queryString;
      } else if (nextArticleKey.value !== undefined) {
        params.params.next = nextArticleKey.value.post_id as string;
        params.params.published_at = nextArticleKey.value
          .published_at as string;
      }

      const data = await $http.get(blockProperties.itemsURL, params);

      if (blockProperties.nextKey) {
        if (
          data.data.next.length !== undefined &&
          data.data.next.length === 0
        ) {
          nextArticleKey.value = undefined;
        } else {
          nextArticleKey.value = data.data.next;
        }
      }

      items.value = data.data;

      if (blockProperties.apiStructure) {
        //Backwards compatibility with new API specs
        for (const item of data.data.data) {
          const itemData: any = {};
          Object.keys(blockProperties.apiStructure).forEach((key) => {
            if (key.endsWith("_feed_id") && name === "article") {
              itemData["article_feed_id"] = activeFeed.value;
            } else {
              itemData[key] = item[blockProperties.apiStructure[key]];
            }
          });
          itemsData.value.push(itemData);
        }
      } else {
        itemsData.value.push(...data.data.data);
      }
    } catch (err) {
      console.error(err);
      notificationsStore.createNotification({
        type: "error",
        title: "Fetching products failed",
        text: "There was an error during fetching products for selected feed.",
      });
    } finally {
      itemLoading.value = false;
    }
  };

  const getFeeds = async (
    queryString?: string | undefined,
    feedId?: string | undefined,
    currentPage?: number
  ) => {
    const blockProperties = apiBlocksStore.blocks.get(name);
    if (blockProperties === undefined) {
      return;
    }

    if (blockProperties.feedsURL === undefined) {
      console.error("Feeds URL is not defined in Topol Options");
      return;
    }

    feedLoading.value = true;

    try {
      const params: IParams = {
        params: {
          search: queryString,
          per_page: 10,
          id: feedId,
        },
      };

      if (!blockProperties.nextKey) {
        params.params.current_page = currentPage;
      } else if (nextArticleKey.value !== undefined) {
        params.params.next = nextFeedKey.value?.post_id;
        params.params.published_at = nextFeedKey.value?.published_at;
      }

      const data = await $http.get(blockProperties.feedsURL, params);

      if (blockProperties.nextKey) {
        // @ts-expect-error
        nextFeedKey.value = data.next;
      }
      feeds.value = data.data;
      feedsData.value.push(...data.data.data);
    } catch (err) {
      console.error(err);
      notificationsStore.createNotification({
        type: "error",
        title: "Fetching feeds failed",
        text: "An error occured while fetching product feeds. Check your feed settings.",
      });
    } finally {
      feedLoading.value = false;
    }
  };

  const getCategories = async (
    queryString?: string | undefined,
    currentPage?: number
  ) => {
    const blockProperties = apiBlocksStore.blocks.get(name);
    if (blockProperties === undefined) {
      return;
    }

    if (blockProperties.categoriesURL === undefined) {
      return;
    }

    categoryLoading.value = true;

    try {
      const params: IParams = {
        params: {
          per_page: 10,
          feed_id: activeFeed.value,
          search: queryString,
          current_page: currentPage,
        },
      };

      const data = await $http.get(blockProperties.categoriesURL, params);

      categories.value = data.data;
      categoriesData.value.push(...data.data.data);
    } catch (err) {
      console.error(err);
      notificationsStore.createNotification({
        type: "error",
        title: "Fetching categories failed",
        text: "An error occurred while fetching categories.",
      });
    } finally {
      categoryLoading.value = false;
    }
  };

  return {
    apiBlock: {
      itemQuery,
      selectedItem,
      feedQuery,
      activeFeed,
      selectedFeed,
      feedsData,
      feeds,
      itemsData,
      items,
      feedLoading,
      itemLoading,
      isInitItem,
      isInitFeed,
      nextArticleKey,
      nextFeedKey,
      categoriesData,
      categories,
      categoryLoading,
      activeCategory,
      productPerPage,
      getItems,
      getFeeds,
      getCategories,
    },
  };
};
